import React, { useState } from "react";
import { Button, Drawer, Collapse, Tag, Image, Skeleton, Tooltip } from "antd";
import { MinusOutlined, PlusOutlined } from "@ant-design/icons";
import { LuExternalLink } from "react-icons/lu";
import VehicleImageModal from "./VehicleImageModal";

const { Panel } = Collapse;

const SeperatorLine = () => {
  return (
    <div
      style={{
        background: "var(--Colors-Neutral-Border-colorBorder, #D9D9D9)",
        height: "1px",
        margin: "8px 0",
      }}
    />
  );
};

// Reusable row component for two-column layout
const InfoRow = ({ label, value, fullWidth = false }) => {
  if (!value && value !== 0) return null;

  return (
    <div className={`flex ${fullWidth ? "w-full" : "w-1/2"} py-1`}>
      <span className="text-xs font-medium text-grey-900 min-w-[120px]">{label}</span>
      <span className="text-xs text-grey-600 flex-1">{value}</span>
    </div>
  );
};

// Section title component matching the screenshot style
const SectionTitle = ({ title }) => (
  <h4 className="text-[14px] font-semibold text-grey-800 tracking-wide mb-2 pb-1 border-b border-grey-200">
    {title}
  </h4>
);

const VehicleInventoryDetailsDrawer = ({
  open,
  onClose,
  vehicleData,
  title = "Vehicle Details",
  placement = "right",
  width = 680,
  className = "vehicle-inventory-details-drawer",
}) => {
  const [activeKey, setActiveKey] = useState(["specifications"]);
  const [imageModalVisible, setImageModalVisible] = useState(false);

  if (!vehicleData) return null;

  // Helper to format price
  const formatPrice = (price, currency = "USD") => {
    if (!price && price !== 0) return "N/A";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "USD",
      minimumFractionDigits: 2,
    }).format(price);
  };

  // Helper to format mileage
  const formatMileage = (mileage, unit) => {
    if (!mileage && mileage !== 0) return "N/A";
    return `${mileage.toLocaleString()} ${unit || "miles"}`;
  };

  // Get vehicle title
  const vehicleTitle = [vehicleData.year, vehicleData.make, vehicleData.model, vehicleData.trim]
    .filter(Boolean)
    .join(" ");

  // Get primary image
  const primaryImage = vehicleData.vehicleInventoryMedias?.[0]?.mediaUrl;

  // Parse extraData if it's a JSON string
  const extraData =
    typeof vehicleData.extraData === "string"
      ? JSON.parse(vehicleData.extraData || "{}")
      : vehicleData.extraData || {};

  // Parse features if it's a JSON string
  const features =
    typeof vehicleData.features === "string"
      ? JSON.parse(vehicleData.features || "[]")
      : vehicleData.features || [];

  const footer = (
    <div className="flex justify-start gap-3">
      <Button onClick={onClose}>Cancel</Button>
    </div>
  );
  return (
    <Drawer
      title={title}
      placement={placement}
      width={width}
      onClose={onClose}
      open={open}
      className={className}
      footer={footer}
    >
      <div className="space-y-4">
        {/* Header with Vehicle Title and Image */}
        <div className="border-b border-grey-200 pb-3">
          <div className="flex gap-4">
            {/* Vehicle Image */}
            <div
              className="flex-shrink-0 cursor-pointer"
              onClick={() => setImageModalVisible(true)}
            >
              <Image
                preview={false}
                placeholder={<Skeleton.Image active style={{ width: 120, height: 90 }} />}
                fallback="https://funnel-webapp-uploads.s3.amazonaws.com/prod/images/generic-car-not-available-image.png"
                style={{
                  width: 120,
                  height: 90,
                  objectFit: "cover",
                  borderRadius: 8,
                }}
                src={primaryImage || ""}
                alt={vehicleTitle}
              />
              {vehicleData.vehicleInventoryMedias?.length > 0 && (
                <div className="text-center text-xs text-blue-600 mt-1">
                  {vehicleData.vehicleInventoryMedias.length} photos
                </div>
              )}
            </div>
            {/* Vehicle Title and Status */}
            <div className="flex-1">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <h2 className="text-[20px] text-[#000000e0] leading-tight">
                    {vehicleTitle || "Unknown Vehicle"}
                    {vehicleData.vdpUrl && (
                      <Tooltip title="View Listing">
                        <span
                          className="ml-2"
                          style={{ display: "inline-flex" }}
                          onClick={e => {
                            e.stopPropagation();
                            if (vehicleData.vdpUrl) {
                              window.open(vehicleData.vdpUrl, "_blank");
                            }
                          }}
                        >
                          <LuExternalLink className="text-base cursor-pointer text-twLinkColor" />
                        </span>
                      </Tooltip>
                    )}
                  </h2>
                </div>
                <Tag color={vehicleData.isNew ? "green" : "orange"} className="ml-2">
                  {vehicleData.isNew ? "New" : "Used"}
                </Tag>
              </div>
              <div className="text-xs text-grey-500 space-y-1">
                <div>Stock #: {vehicleData.stockNumber || "N/A"}</div>
                <div className="font-mono">VIN: {vehicleData.vin || "N/A"}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Summary Section */}
        <div
          className="p-3 rounded-lg"
          style={{
            background: "var(--Colors-Neutral-Bg-colorBgLayout, #F5F5F5)",
          }}
        >
          <div className="flex flex-wrap">
            <InfoRow label="Condition" value={vehicleData.isNew ? "New" : "Used"} />
            <InfoRow label="Stock Number" value={vehicleData.stockNumber} />
            <InfoRow label="Body Style" value={vehicleData.bodyStyle} />
            <InfoRow label="Vehicle Type" value={vehicleData.vehicleType} />
            <InfoRow label="Model Code" value={vehicleData.modelNumber} />
            <InfoRow label="VIN" value={vehicleData.vin} />
            <InfoRow label="Exterior Color" value={vehicleData.exteriorColor} />
            <InfoRow label="Interior Color" value={vehicleData.interiorColor} />
          </div>
        </div>

        {/* Specifications Collapse Section */}
        <Collapse
          activeKey={activeKey}
          onChange={setActiveKey}
          expandIcon={({ isActive }) => (isActive ? <MinusOutlined /> : <PlusOutlined />)}
          expandIconPosition="end"
          className="bg-white border border-grey-200 rounded-lg"
        >
          <Panel
            header={
              <span className="text-[14px] font-semibold text-grey-800 tracking-wide">
                Specifications
              </span>
            }
            key="specifications"
            className="specifications-panel"
          >
            {/* General Information */}
            <div className="mb-4">
              <SectionTitle title="General Information" />
              <div className="flex flex-wrap pl-2">
                <InfoRow label="Manufacturer" value={vehicleData.make} />
                <InfoRow label="Model Year" value={vehicleData.year} />
                <InfoRow label="Model" value={vehicleData.model} />
                <InfoRow label="Model Code" value={vehicleData.modelNumber} />
                <InfoRow label="Stock Number" value={vehicleData.stockNumber} />
                <InfoRow label="Trim" value={vehicleData.trim} />
                <InfoRow label="Exterior Color" value={vehicleData.exteriorColor} />
                <InfoRow label="Interior Color" value={vehicleData.interiorColor} />
                {vehicleData.isCertified && <InfoRow label="Certified" value="Yes" />}
              </div>
            </div>

            <SeperatorLine />

            {/* Dimensions & Capacity */}
            {(vehicleData.mileage || vehicleData.doors || vehicleData.seats) && (
              <>
                <div className="mb-4">
                  <SectionTitle title="Dimensions & Capacity" />
                  <div className="flex flex-wrap pl-2">
                    <InfoRow
                      label="Mileage"
                      value={formatMileage(vehicleData.mileage, vehicleData.mileageUnit)}
                    />
                    <InfoRow label="Doors" value={vehicleData.doors} />
                    <InfoRow label="Seats" value={vehicleData.seats} />
                  </div>
                </div>
                <SeperatorLine />
              </>
            )}

            {/* Engine & Transmission */}
            {(vehicleData.engineCylinders ||
              vehicleData.transmission ||
              vehicleData.driveTrain ||
              vehicleData.driveType) && (
              <>
                <div className="mb-4">
                  <SectionTitle title="Engine & Transmission" />
                  <div className="flex flex-wrap pl-2">
                    <InfoRow
                      label="Engine"
                      value={
                        vehicleData.engineCylinders
                          ? `${vehicleData.engineCylinders} Cylinders`
                          : null
                      }
                    />
                    <InfoRow label="Transmission" value={vehicleData.transmission} />
                    {vehicleData.transmissionDesc && (
                      <InfoRow
                        label="Transmission Desc"
                        value={vehicleData.transmissionDesc}
                        fullWidth
                      />
                    )}
                    <InfoRow label="Drive Train" value={vehicleData.driveTrain} />
                    <InfoRow label="Drive Type" value={vehicleData.driveType} />
                  </div>
                </div>
                <SeperatorLine />
              </>
            )}

            {/* Fuel Economy */}
            {(vehicleData.fuelType || vehicleData.epaCombined || vehicleData.range) && (
              <>
                <div className="mb-4">
                  <SectionTitle title="Fuel Economy" />
                  <div className="flex flex-wrap pl-2">
                    <InfoRow label="Fuel Type" value={vehicleData.fuelType} />
                    {vehicleData.epaCity && (
                      <InfoRow
                        label="EPA City"
                        value={`${vehicleData.epaCity} ${vehicleData.epaUnit || "MPG"}`}
                      />
                    )}
                    {vehicleData.epaHighway && (
                      <InfoRow
                        label="EPA Highway"
                        value={`${vehicleData.epaHighway} ${vehicleData.epaUnit || "MPG"}`}
                      />
                    )}
                    {vehicleData.epaCombined && (
                      <InfoRow
                        label="EPA Combined"
                        value={`${vehicleData.epaCombined} ${vehicleData.epaUnit || "MPG"}`}
                      />
                    )}
                    {vehicleData.range && (
                      <InfoRow
                        label="Range"
                        value={`${vehicleData.range} ${vehicleData.rangeUnit || "miles"}`}
                      />
                    )}
                  </div>
                </div>
                <SeperatorLine />
              </>
            )}

            {/* Pricing */}
            <div className="mb-4">
              <SectionTitle title="Pricing" />
              <div className="flex flex-wrap pl-2">
                <InfoRow
                  label="MSRP"
                  value={formatPrice(vehicleData.msrp, vehicleData.currencyUnit)}
                />
                <InfoRow
                  label="Selling Price"
                  value={formatPrice(vehicleData.sellingPrice, vehicleData.currencyUnit)}
                />
                {vehicleData.invoiceAmount && (
                  <InfoRow
                    label="Invoice Amount"
                    value={formatPrice(vehicleData.invoiceAmount, vehicleData.currencyUnit)}
                  />
                )}
              </div>
            </div>

            {/* Availability */}
            {vehicleData.availability && (
              <>
                <SeperatorLine />
                <div className="mb-4">
                  <SectionTitle title="Availability" />
                  <div className="flex flex-wrap pl-2">
                    <InfoRow label="Status" value={vehicleData.availability} fullWidth />
                  </div>
                </div>
              </>
            )}

            {/* Features */}
            {features && features.length > 0 && (
              <>
                <SeperatorLine />
                <div className="mb-4">
                  <SectionTitle title="Features" />
                  <div className="pl-2">
                    <div className="flex flex-wrap gap-1">
                      {features.map((feature, index) => (
                        <Tag key={index} className="text-xs mb-1">
                          {typeof feature === "string" ? feature : feature.name || feature.feature}
                        </Tag>
                      ))}
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Description / Comments */}
            {(vehicleData.description || vehicleData.comments) && (
              <>
                <SeperatorLine />
                <div className="mb-4">
                  <SectionTitle title="Description" />
                  <div className="pl-2">
                    {vehicleData.description && (
                      <div
                        className="text-xs text-grey-600 mb-2 [&>p]:mb-1 [&>ul]:list-disc [&>ul]:pl-4 [&>ol]:list-decimal [&>ol]:pl-4"
                        dangerouslySetInnerHTML={{ __html: vehicleData.description }}
                      />
                    )}
                    {vehicleData.comments && (
                      <div
                        className="text-xs text-grey-500 italic [&>p]:mb-1 [&>ul]:list-disc [&>ul]:pl-4 [&>ol]:list-decimal [&>ol]:pl-4"
                        dangerouslySetInnerHTML={{ __html: vehicleData.comments }}
                      />
                    )}
                  </div>
                </div>
              </>
            )}

            {/* Extra Data (if available) */}
            {extraData && Object.keys(extraData).length > 0 && (
              <>
                <SeperatorLine />
                <div className="mb-4">
                  <SectionTitle title="Additional Information" />
                  <div className="flex flex-wrap pl-2">
                    {Object.entries(extraData).map(([key, value]) => {
                      if (!value || typeof value === "object") return null;
                      const formattedKey = key
                        .replace(/([A-Z])/g, " $1")
                        .replace(/_/g, " ")
                        .replace(/^./, str => str.toUpperCase())
                        .trim();
                      const stringValue = String(value);
                      const isUrl = /^https?:\/\//i.test(stringValue);
                      return (
                        <InfoRow
                          key={key}
                          label={formattedKey}
                          value={
                            isUrl ? (
                              <a
                                href={stringValue}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-800 hover:underline ml-1"
                              >
                                Click to view
                              </a>
                            ) : (
                              stringValue
                            )
                          }
                        />
                      );
                    })}
                  </div>
                </div>
              </>
            )}
          </Panel>
        </Collapse>
      </div>

      {/* Vehicle Image Modal */}
      <VehicleImageModal
        visible={imageModalVisible}
        onClose={() => setImageModalVisible(false)}
        images={vehicleData.vehicleInventoryMedias || []}
        vehicleInfo={{
          year: vehicleData.year,
          make: vehicleData.make,
          model: vehicleData.model,
          stockNumber: vehicleData.stockNumber,
        }}
      />
    </Drawer>
  );
};

export default VehicleInventoryDetailsDrawer;
